import { Controller } from "@hotwired/stimulus"

// Orchestrates the admin "new subscription" form: student/service/plan selection, currency &
// amount, and validation. The date-option modal itself is the SAME state-aware partial the
// student flow uses — fetched per (student, plan, service) and injected into dateModalContainer,
// driven by the subscription-options controller. This controller only bridges the chosen dates
// (and a suggested, overridable amount) back into the form fields.
export default class extends Controller {
  static targets = [
    "serviceSelect",
    "planSelect",
    "planSearch",
    "contactNumber",
    "planInfoRow",
    "planClassesSummary",
    "planAmount",
    "validFrom",
    "validTill",
    "amountMajor",
    "currencySelect",
    "datesSummary",
    "datesSummaryText",
    "submitButton",
    "submitHint",
    "dateModalContainer"
  ]

  connect() {
    this._amountDirty = false
    this._datesConfirmed = false
    if (this.hasValidFromTarget) this.validFromTarget.value = ""
    if (this.hasValidTillTarget) this.validTillTarget.value = ""
    this.loadPlans()
    this.validate()
  }

  // ─── Student selection ───────────────────────────────────────────────────

  async studentChanged(event) {
    const id = event.target.value
    if (!id) { this.contactNumberTarget.value = ""; return }
    try {
      const res = await fetch(`/admin/students/${id}/contact`, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      this.contactNumberTarget.value = data?.phone_number || ""
      // Default the currency to the student's saved preference (falls back to whatever's selected).
      if (this.hasCurrencySelectTarget && data?.preferred_currency) {
        this.currencySelectTarget.value = data.preferred_currency
        this.currencyChanged()
      }
      this.validate()
    } catch { /* silent */ }
  }

  // ─── Plan loading & selection ─────────────────────────────────────────────

  serviceChanged() {
    const service = this.hasServiceSelectTarget ? this.serviceSelectTarget.value : ""
    const typeMap = { group_classes: "hybrid_only", personal_classes: "online_only", workshops: "online_only" }
    const targetType = typeMap[service]
    if (targetType) {
      const radio = this.element.querySelector(`input[name='plan_access_type'][value='${targetType}']`)
      if (radio) radio.checked = true
    }
    this.filtersChanged()
  }

  async filtersChanged() {
    await this.loadPlans()
    this.filterPlans()
  }

  async loadPlans() {
    if (!this.hasPlanSelectTarget) return
    const service = this.hasServiceSelectTarget ? this.serviceSelectTarget.value : ""
    if (!service) {
      this.planSelectTarget.innerHTML = `<option value="">Select plan</option>`
      this.planClassesSummaryTarget.textContent = "—"
      this.planAmountTarget.textContent = "—"
      return
    }
    this.planSelectTarget.innerHTML = `<option value="">Select plan</option>`
    this.planClassesSummaryTarget.textContent = "—"
    this.planAmountTarget.textContent = "—"
    const url = new URL("/admin/plans/available", window.location.origin)
    if (service) url.searchParams.set("service", service)
    try {
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const plans = await res.json()
      plans.forEach((p) => {
        const opt = document.createElement("option")
        opt.value = p.id
        opt.textContent = p.label || p.name
        if (p.label) opt.dataset.searchText = String(p.label).toLowerCase()
        this.planSelectTarget.appendChild(opt)
      })
      this.filterPlans()
    } catch { /* silent */ }
  }

  filterPlans() {
    if (!this.hasPlanSearchTarget || !this.hasPlanSelectTarget) return
    const q = (this.planSearchTarget.value || "").trim().toLowerCase()
    Array.from(this.planSelectTarget.options).forEach((opt, idx) => {
      if (idx === 0) return
      const text = (opt.dataset.searchText || opt.textContent || "").toLowerCase()
      opt.hidden = q ? !text.includes(q) : false
    })
  }

  async planChanged(event) {
    const id = event.target.value
    if (!id) return
    // Reset any previously chosen dates — they belong to the old plan.
    this._datesConfirmed = false
    if (this.hasValidFromTarget) this.validFromTarget.value = ""
    if (this.hasValidTillTarget) this.validTillTarget.value = ""
    this._refreshDatesSummary()

    const url = new URL(`/admin/plans/${id}/subscription_preview`, window.location.origin)
    try {
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      if (this.hasPlanInfoRowTarget) this.planInfoRowTarget.classList.remove("hidden")
      this.planClassesSummaryTarget.textContent = data?.term_and_classes_display || "—"
      this.planClassesSummaryTarget.classList.remove("hidden")
      this.planAmountTarget.textContent = data?.amount_formatted || "—"
      this.planAmountTarget.classList.remove("hidden")
      // Full monthly price per currency — basis for the suggested (pro-rated) amount.
      this._planFullPricing = {
        INR: Number(data?.amount_inr_cents || 0),
        EUR: Number(data?.amount_eur_cents || 0),
        USD: Number(data?.amount_usd_cents || 0)
      }
      this._lastPlanPricing = { ...this._planFullPricing }
      this._applyPlanAmount()
      this.validate()
    } catch { /* silent */ }
  }

  // ─── Currency & amount ────────────────────────────────────────────────────

  currencyChanged() {
    if (!this.hasAmountMajorTarget) return
    this._applyPlanAmount()
    this.amountChanged()
  }

  amountEdited() {
    const raw = (this.amountMajorTarget?.value || "").trim()
    this._amountDirty = raw.length > 0
    this.amountChanged()
  }

  amountChanged() { this.validate() }

  _applyPlanAmount() {
    if (!this.hasAmountMajorTarget || this._amountDirty) return
    const code = this._selectedCurrency()
    const cents = this._lastPlanPricing?.[code] || 0
    const fallback = this._lastPlanPricing?.INR || 0
    const chosen = cents > 0 ? cents : fallback
    if (chosen > 0) {
      this.amountMajorTarget.value = (chosen / 100).toFixed(2)
      this.validate()
    }
  }

  // ─── Date options modal (shared partial) ──────────────────────────────────

  async openDateOptions() {
    const studentId = this.element.querySelector("select[name='subscription[user_id]']")?.value
    const service   = this.element.querySelector("select[name='subscription[service]']")?.value
    const planId    = this.hasPlanSelectTarget ? this.planSelectTarget.value : ""
    if (!studentId || !service || !planId) {
      if (this.hasSubmitHintTarget) this.submitHintTarget.textContent = "Select a student, service and plan first."
      return
    }
    if (this.hasSubmitHintTarget) this.submitHintTarget.textContent = ""

    const currency = this._selectedCurrency()
    const url = new URL("/admin/subscriptions/date_options", window.location.origin)
    url.searchParams.set("user_id", studentId)
    url.searchParams.set("plan_id", planId)
    url.searchParams.set("service", service)
    url.searchParams.set("currency", currency)
    try {
      const res = await fetch(url.toString(), { headers: { Accept: "text/html" } })
      if (!res.ok) return
      const html = await res.text()

      const previewUrl = new URL("/admin/subscriptions/date_preview", window.location.origin)
      previewUrl.searchParams.set("user_id", studentId)
      previewUrl.searchParams.set("service", service)
      previewUrl.searchParams.set("currency", currency)

      const wrapper = document.createElement("div")
      wrapper.dataset.controller = "subscription-options"
      wrapper.dataset.subscriptionOptionsPlanIdValue = String(planId)
      wrapper.dataset.subscriptionOptionsCurrencyValue = this._selectedCurrency()
      wrapper.dataset.subscriptionOptionsPreviewUrlValue = previewUrl.pathname + previewUrl.search
      wrapper.dataset.subscriptionOptionsAutoOpenValue = "true"
      wrapper.innerHTML = html
      this.dateModalContainerTarget.replaceChildren(wrapper)
    } catch { /* silent */ }
  }

  // Fired by the injected subscription-options controller whenever the chosen option changes.
  datesChanged(event) {
    const d = event.detail
    if (!d || d.valid === false || !d.startDate || !d.endDate) return
    if (this.hasValidFromTarget) this.validFromTarget.value = d.startDate
    if (this.hasValidTillTarget) this.validTillTarget.value = d.endDate
    this._suggestAmount(Number(d.amountCents), Number(d.monthlyFeeCents))
    this.validate()
  }

  // Admin confirm for the standard / past-dues flows (dates already set by datesChanged).
  confirmDates() {
    if (!this.hasValidFromTarget || !this.hasValidTillTarget) return
    if (!this.validFromTarget.value || !this.validTillTarget.value) return
    this._datesConfirmed = true
    this._refreshDatesSummary()
    this.validate()
  }

  // Usage-based personal pack: fixed dates carried on the confirm button.
  confirmPersonalPack(event) {
    const b = event.currentTarget
    if (this.hasValidFromTarget) this.validFromTarget.value = b.dataset.startDate
    if (this.hasValidTillTarget) this.validTillTarget.value = b.dataset.endDate
    const cents = Number(b.dataset.amountCents)
    this._suggestAmount(cents, cents) // full price, no pro-ration
    this._datesConfirmed = true
    this._refreshDatesSummary()
    this.validate()
  }

  // Turns the engine's INR pro-rated/monthly amounts into a suggested amount for the selected
  // currency, then defers to _applyPlanAmount (which respects a manually-edited amount).
  _suggestAmount(proratedInr, monthlyInr) {
    const full = this._planFullPricing || { INR: monthlyInr, EUR: 0, USD: 0 }
    const ratio = monthlyInr > 0 ? proratedInr / monthlyInr : 1
    this._lastPlanPricing = {
      INR: Math.round(ratio * Number(full.INR || monthlyInr || 0)),
      EUR: Math.round(ratio * Number(full.EUR || 0)),
      USD: Math.round(ratio * Number(full.USD || 0))
    }
    this._applyPlanAmount()
  }

  // ─── Summary & validation ─────────────────────────────────────────────────

  _refreshDatesSummary() {
    if (!this.hasDatesSummaryTarget || !this.hasDatesSummaryTextTarget) return
    const from = this.hasValidFromTarget ? this.validFromTarget.value : ""
    const till = this.hasValidTillTarget ? this.validTillTarget.value : ""
    if (!from || !till) { this.datesSummaryTarget.classList.add("hidden"); return }
    const s = new Date(`${from}T00:00:00`)
    this.datesSummaryTextTarget.textContent = this._isSentinel(till)
      ? `Starts ${this._formatDDMMYY(s)} · No expiry (valid until classes used up)`
      : `Starts ${this._formatDDMMYY(s)} · Ends ${this._formatDDMMYY(new Date(`${till}T00:00:00`))}`
    this.datesSummaryTarget.classList.remove("hidden")
  }

  validate() {
    if (!this.hasSubmitButtonTarget) return
    const studentOk = !!this.element.querySelector("select[name='subscription[user_id]']")?.value
    const serviceOk = !!this.element.querySelector("select[name='subscription[service]']")?.value
    const planOk    = !!this.element.querySelector("select[name='subscription[plan_id]']")?.value
    const datesOk   = this.hasValidFromTarget && this.hasValidTillTarget &&
                      !!this.validFromTarget.value && !!this.validTillTarget.value
    const amountOk  = this._readAmountMajor() > 0
    const ok = studentOk && serviceOk && planOk && datesOk && amountOk
    this.submitButtonTarget.disabled = !ok
    if (this.hasSubmitHintTarget) this.submitHintTarget.textContent = ""
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  _isSentinel(iso) { return String(iso).startsWith("9999") }

  _formatDDMMYY(dateObj) {
    const dd = String(dateObj.getDate()).padStart(2, "0")
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0")
    const yy = String(dateObj.getFullYear()).slice(-2)
    return `${dd}/${mm}/${yy}`
  }

  _readAmountMajor() {
    if (!this.hasAmountMajorTarget) return 0
    const n = Number.parseFloat((this.amountMajorTarget.value || "").trim())
    return (isNaN(n) || n <= 0) ? 0 : Math.round(n)
  }

  _selectedCurrency() {
    if (this.hasCurrencySelectTarget) return this.currencySelectTarget.value || "INR"
    return this.element.querySelector("select[name='subscription[currency]']")?.value || "INR"
  }
}
